// JS of InfoPopup
import Overlay from '../../shared/global/Overlay';
import { initializeModules } from '../../shared/global/importer';
import { APP_CONFIG } from '../../shared/global/base';
import Loader from '../../shared/utils/loader';

type InfoPopupData = {
    current: Overlay,
    containerDOM: HTMLElement,
    target: HTMLElement,
    loading: boolean,
    loader: Loader,
    template: HTMLTemplateElement,
    onclose: (() => void)[],
    partials: PartialsList
}

type InfoPopupOption = {
    text: string | HTMLElement;
    header: string;
    subtitle: string;
    button: string;
    icon: string;
    image: string;
    url: string;
    html: string;
    preventmoduleinit: boolean;
    partial: PartialName;
    confirmMode: boolean;
}

const OVERLAY_DATA:InfoPopupData = {
    current: null,
    containerDOM: null,
    target: null,
    loading: false,
    loader: new Loader(),
    template: null,
    onclose: [],
    partials: APP_CONFIG.partials
};

const SELECTORS = {
    overlay: 'm-InfoPopup',
    module: 'InfoPopup',
    showpopup: 'infoPopup-visible',
    popuptrigger: '[data-js=openInfoPopup]'
};



export {
    initAfterDynamicImport,
    showInfoPopup,
    closeInfoPopup,
    subscribeToClose
};


function initAfterDynamicImport(dom: HTMLFormElement){
    OVERLAY_DATA.containerDOM = dom;
    // check options
    const showOnLoad = OVERLAY_DATA.containerDOM.hasAttribute('data-showonload');
    if (showOnLoad) {
        showInfoPopup({ preventmoduleinit: true });
    }
    // init triggers in document
    initTriggers(SELECTORS.popuptrigger);
}


/**
 * Init partial openers
 * @param {string} selector - optional
 * @returns {undefined}
 */
function initTriggers(selector: string){
    let _popupTriggers = document.querySelectorAll(selector);
    if( _popupTriggers.length ){
        _popupTriggers.forEach((el)=>{
            el.addEventListener('click', e => {
                e.preventDefault();
                const _header = el.getAttribute('data-header');
                const _text = el.getAttribute('data-text');
                const _btnText = el.getAttribute('data-btn');
                const _subtitle = el.getAttribute('data-subtitle');
                const _icon = el.getAttribute('data-icon');
                const _image = el.getAttribute( 'data-image' );
                const _partial = el.getAttribute('data-partial') as PartialName;
                showInfoPopup({
                    header: _header,
                    subtitle: _subtitle,
                    text: _text,
                    button: _btnText,
                    icon: _icon,
                    image: _image,
                    partial: _partial
                });
            });
        });
    }
}


function buildContainer() {
    if (!OVERLAY_DATA.containerDOM) {
        OVERLAY_DATA.containerDOM = document.querySelector(`[data-jsmodule=${SELECTORS.module}]`);
    }
    if (OVERLAY_DATA.containerDOM) {
        if (!OVERLAY_DATA.target) {
            OVERLAY_DATA.target = OVERLAY_DATA.containerDOM.querySelector(`[data-${SELECTORS.module.toLowerCase()}-container]`);
            OVERLAY_DATA.template = OVERLAY_DATA.containerDOM.querySelector(`[data-${SELECTORS.module.toLowerCase()}-message]`);

            OVERLAY_DATA.containerDOM.addEventListener('click', overlayClickHandler);
        }

        if (!OVERLAY_DATA.target) {
            return false
        }

        return true;
    }

    return false
}


/**
 * Create new popup with content from partial or prepared template
 * @param {InfoPopupOption} param0
 */
function showInfoPopup({ header, subtitle, text, button, icon, image, url, preventmoduleinit, partial, confirmMode }: Partial<InfoPopupOption>) {
    if (!OVERLAY_DATA.loading) {
        OVERLAY_DATA.loading = true;
        // Remove callbacks for previous popup
        OVERLAY_DATA.onclose = [];

        // check container
        if (buildContainer()) {
            // setup new overlay (always new)
            OVERLAY_DATA.current = new Overlay('infopopup', {
                overlayContainer: OVERLAY_DATA.containerDOM?.parentNode,
                overlayClass: SELECTORS.overlay,
                showClass: SELECTORS.showpopup
            });

            if (partial || url) {
                OVERLAY_DATA.loader.show(OVERLAY_DATA.target.parentElement, false);
                if (partial) {
                    // Get partial url if partial is specified correctly
                    if (OVERLAY_DATA.partials[partial] && OVERLAY_DATA.partials[partial].partial) {
                        url = OVERLAY_DATA.partials[partial].partial + (url ? url : '');
                    }
                    else {
                        console.log('%cPartial: '+ partial + ' nie istnieje w konfiguracji', 'color:red');
                    }
                }

                // Load external content into popup
                OVERLAY_DATA.current.createAjax(
                    url,
                    OVERLAY_DATA.target,
                    overlayLoaded.bind(null, preventmoduleinit),
                    overlayLoadingFailed
                );
            }
            else {
                // fill in template and show it in popup
                const template = prepareTemplateMessage({
                    header,
                    subtitle,
                    text,
                    button,
                    icon,
                    image,
                    confirmMode
                });

                if (template){
                    // reset html content and upload new one
                    OVERLAY_DATA.target.innerHTML = '';
                    OVERLAY_DATA.target.appendChild(template);
                    // this is because of svg sprite, it won't render from template properly
                    OVERLAY_DATA.target.innerHTML = OVERLAY_DATA.target.innerHTML;
                }

                // show popup
                OVERLAY_DATA.current.create(
                    overlayLoaded.bind(null, preventmoduleinit)
                );
            }
        }
        else {
            overlayLoadingFailed();
        }
    }
}

/**
 * Parse template and fill in with content
 * @param {InfoPopupOption} param0
 */
function prepareTemplateMessage({ header, subtitle, text, button, icon, image, confirmMode }: Partial<InfoPopupOption>): Node {
    if ((header || subtitle || text || button) && OVERLAY_DATA.template){
        const template = OVERLAY_DATA.template.content.cloneNode(true) as HTMLElement;
        const hdr = template.querySelector('[data-messagepopup-hdr]') as HTMLElement;
        const sub = template.querySelector('[data-messagepopup-subtitle]') as HTMLElement;
        const txt = template.querySelector('[data-messagepopup-text]') as HTMLElement;
        const btn = template.querySelector('[data-messagepopup-btn]') as HTMLElement;
        const ico = template.querySelector('[data-messagepopup-icon]') as HTMLElement;

        const confirmAttr = `data-${SELECTORS.module.toLowerCase()}-confirm`;

        // header or smaller subtitle
        sub.style.display = 'none';
        hdr.style.display = 'none';
        if (header) {
            hdr.textContent = header;
            hdr.style.display = '';
        }
        else if (subtitle) {
            sub.textContent = subtitle;
            sub.style.display = '';
        }

        // text
        if (text) {
            txt.innerHTML = `${text}`;
            txt.style.display = '';
        }
        else {
            txt.style.display = 'none';
        }

        // icon / image
        if (icon) {
            ico.innerHTML = `<svg><use xlink:href="${icon}"></use></svg>`;
            ico.style.display = '';
        }
        else if (image) {
            const img = document.createElement('img');
            img.alt = '';
            img.src = image;
            ico.appendChild(img);
            ico.style.display = '';
        }
        else {
            ico.style.display = 'none';
        }

        // button
        btn.textContent = button || 'OK';

        // Used for additional params when closing
        if (confirmMode) {
            btn.setAttribute(confirmAttr, '');
        } else {
            btn.removeAttribute(confirmAttr);
        }

        return template;
    }

    return null;
}



function overlayLoaded(preventmoduleinit: boolean) {
    // initialise overlay functions
    OVERLAY_DATA.loader.hide();

    OVERLAY_DATA.loading = false;

    if (!preventmoduleinit) {
        initializeModules(OVERLAY_DATA.target);
    }
}

function overlayLoadingFailed() {
    // console.log("failed");
    OVERLAY_DATA.loader.hide();

    OVERLAY_DATA.loading = false;
}

function closeInfoPopup(params?: any) {
    if (OVERLAY_DATA.current) {
        OVERLAY_DATA.current.hide();
        OVERLAY_DATA.current = null;

        OVERLAY_DATA.onclose.map( (f: (p?: any) => void) => f(params));
        OVERLAY_DATA.onclose = [];
    }
}

function overlayClickHandler(e: MouseEvent) {
    const _target = e.target as HTMLElement;

    if (_target.hasAttribute(`data-${SELECTORS.module.toLowerCase()}-confirm`)) {
        e.preventDefault();
        closeInfoPopup({ force: true });
    }
    else if (_target.hasAttribute('data-jselement-popupclose')) {
        e.preventDefault();
        closeInfoPopup();
    }
}

/**
 * Add callback invoked after popup close
 * @param callback
 */
function subscribeToClose(callback: (p?: any) => void) {
    if (OVERLAY_DATA.current) {
        OVERLAY_DATA.onclose.push(callback);
    }
}
